import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  ANALYZER_PROTOCOL_LABELS,
  type AnalyzerDriverConfig,
  type AnalyzerProtocol,
  type AnalyzerView,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  EmptyState,
  Notice,
  RadioGroup,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  addAnalyzer,
  analyzerKeys,
  analyzerMessagesQuery,
  analyzersQuery,
  retireAnalyzer,
  rotateAnalyzerCredential,
} from '../api/lab-analyzers.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { AnalyzerDriverForm } from '../lab/analyzer-driver-form.tsx';
import {
  CONNECTION_NEXT_STEP,
  ConnectionTag,
  CredentialShownOnce,
  lastSeenText,
  protocolText,
} from '../lab/analyzer-state.tsx';
import { AnalyzerTestCodes } from '../lab/analyzer-test-codes.tsx';
import styles from '../lab/lab.module.css';
import { TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { LabSettingsFrame, QueryState } from './settings-lab.tsx';

// Settings > Lab > Analyzers (docs/plans/phase2-lab-connector.md §6; ADR 0026). Each analyzer
// has its own credential, shown once; its connector reports its state every minute. Results
// it sends land as unvalidated results, or wait for review in the lab.

const DEFAULT_DRIVER: Record<AnalyzerProtocol, AnalyzerDriverConfig> = {
  astm_e1394: {
    protocol: 'astm_e1394',
    specimenIdField: 'O.3',
    specimenIdComponent: 1,
    testCodeComponent: 4,
    utcOffset: '+03:00',
  },
  hl7_v2: {
    protocol: 'hl7_v2',
    specimenIdField: 'OBR-3',
    specimenIdComponent: 1,
    testCodeComponent: 1,
    utcOffset: '+03:00',
  },
};

export function LabAnalyzersPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const analyzers = useQuery({ ...analyzersQuery, refetchInterval: 30_000 });
  const [adding, setAdding] = useState(false);
  const mayManage = session !== null && can(session, 'lab.analyzers.manage');
  return (
    <LabSettingsFrame title="Lab">
      <Section
        title="Analyzers"
        description="Analyzers connected through the lab connector. Their results arrive unvalidated, for a technologist to check."
        actions={
          mayManage ? (
            <Button
              isDisabled={!online}
              onPress={() => {
                setAdding(true);
              }}
            >
              Add analyzer
            </Button>
          ) : undefined
        }
      >
        <QueryState query={analyzers} what="analyzers" />
        {analyzers.isSuccess && analyzers.data.length === 0 ? (
          <EmptyState>
            No analyzers yet. Add one, then install the lab connector with its credential.
          </EmptyState>
        ) : null}
        {analyzers.isSuccess && analyzers.data.length > 0 ? (
          <TableScroll>
            <Table aria-label="Analyzers" flat>
              <TableHeader>
                <Column isRowHeader>Analyzer</Column>
                <Column>Protocol</Column>
                <Column>Connection</Column>
                <Column>Last message</Column>
                <Column numeric>To review</Column>
                <Column>Last problem</Column>
              </TableHeader>
              <TableBody items={analyzers.data}>
                {(a) => (
                  <Row id={a.id}>
                    <Cell>
                      <Link to="/settings/lab/analyzers/$analyzerId" params={{ analyzerId: a.id }}>
                        {a.name}
                      </Link>
                    </Cell>
                    <Cell>{protocolText(a)}</Cell>
                    <Cell>
                      <span className={styles.stack}>
                        <ConnectionTag analyzer={a} />
                        <span className={styles.meta}>{lastSeenText(a)}</span>
                      </span>
                    </Cell>
                    <Cell>
                      {a.lastMessageAt === null ? 'None yet' : formatInstant(a.lastMessageAt)}
                    </Cell>
                    <Cell numeric>{String(a.queued)}</Cell>
                    <Cell>{a.lastError === null ? 'None' : a.lastError.text}</Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        ) : null}
      </Section>
      {adding ? (
        <AddAnalyzerDialog
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}
    </LabSettingsFrame>
  );
}

function AddAnalyzerDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [protocol, setProtocol] = useState<AnalyzerProtocol>('astm_e1394');
  const [tried, setTried] = useState(false);
  const [credential, setCredential] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => addAnalyzer({ name: name.trim(), driver: DEFAULT_DRIVER[protocol] }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: analyzerKeys.all });
      setCredential(created.credential);
      toasts.show({ title: 'Analyzer added' });
    },
  });
  return (
    <Dialog
      title={credential === null ? 'Add analyzer' : 'Analyzer added'}
      isOpen
      // While the credential shows, this is its only copy: only Done closes the dialog.
      isDismissable={credential === null}
      isKeyboardDismissDisabled={credential !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {credential === null ? (
        <div className={styles.form}>
          <TextField
            label="Name"
            description="As the lab calls it, for example the bench and model."
            value={name}
            onChange={setName}
            autoFocus
            {...(tried && name.trim() === '' ? { errorMessage: 'Give the analyzer a name.' } : {})}
          />
          <RadioGroup
            label="Protocol"
            description="From the analyzer's interface manual. Where it puts the sample number can be changed after adding it."
            value={protocol}
            options={(Object.keys(ANALYZER_PROTOCOL_LABELS) as AnalyzerProtocol[]).map((p) => ({
              id: p,
              label: ANALYZER_PROTOCOL_LABELS[p],
            }))}
            onChange={(value) => {
              setProtocol(value as AnalyzerProtocol);
            }}
          />
          {add.isError ? (
            <Notice tone="error">
              {errorText(add.error, "The analyzer wasn't added. Try again.")}
            </Notice>
          ) : null}
        </div>
      ) : (
        <div className={styles.form}>
          <CredentialShownOnce credential={credential} />
        </div>
      )}
      <DialogActions>
        {credential === null ? (
          <>
            <Button variant="quiet" onPress={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isPending={add.isPending}
              pendingLabel="Adding"
              onPress={() => {
                setTried(true);
                if (name.trim() !== '') add.mutate();
              }}
            >
              Add analyzer
            </Button>
          </>
        ) : (
          <Button variant="primary" onPress={onClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

const analyzerRoute = getRouteApi('/_app/settings/lab/analyzers/$analyzerId');

export function LabAnalyzerPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const { analyzerId } = analyzerRoute.useParams();
  const analyzers = useQuery({ ...analyzersQuery, refetchInterval: 30_000 });
  const analyzer = analyzers.data?.find((a) => a.id === analyzerId);
  const mayManage =
    session !== null && can(session, 'lab.analyzers.manage') && analyzer?.status === 'active';
  return (
    <LabSettingsFrame title={analyzer?.name ?? 'Analyzer'}>
      <p>
        <Link to="/settings/lab/analyzers">All analyzers</Link>
      </p>
      <QueryState query={analyzers} what="analyzer" />
      {analyzers.isSuccess && analyzer === undefined ? (
        <Notice tone="error">This analyzer doesn&apos;t exist at this facility.</Notice>
      ) : null}
      {analyzer === undefined ? null : (
        <>
          <ConnectionSection analyzer={analyzer} mayManage={mayManage} />
          <Section
            title="How to read its messages"
            description="Where this analyzer puts the sample number and the test code. Check against its interface manual before go-live."
          >
            <AnalyzerDriverForm analyzer={analyzer} editable={mayManage} />
          </Section>
          <Section
            title="Test codes"
            description="Each code the analyzer sends, the analyte it is, and the unit it sends. Results for codes not listed wait for review."
          >
            <AnalyzerTestCodes analyzer={analyzer} editable={mayManage} />
          </Section>
          <MessageLog analyzerId={analyzer.id} />
        </>
      )}
    </LabSettingsFrame>
  );
}

function ConnectionSection({
  analyzer,
  mayManage,
}: {
  analyzer: AnalyzerView;
  mayManage: boolean;
}) {
  const online = useOnline();
  const [rotating, setRotating] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const next =
    analyzer.status === 'active' ? CONNECTION_NEXT_STEP[analyzer.connection.state] : null;
  return (
    <Section
      title="Connection"
      actions={
        mayManage ? (
          <span className={styles.actions}>
            <Button
              isDisabled={!online}
              onPress={() => {
                setRotating(true);
              }}
            >
              Rotate credential
            </Button>
            <Button
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                setRetiring(true);
              }}
            >
              Retire
            </Button>
          </span>
        ) : undefined
      }
    >
      <dl className={styles.factGrid}>
        <dt>State</dt>
        <dd>
          <ConnectionTag analyzer={analyzer} />
        </dd>
        <dt>Protocol</dt>
        <dd>{protocolText(analyzer)}</dd>
        <dt>Connector</dt>
        <dd>
          {analyzer.connection.connectorVersion === null
            ? lastSeenText(analyzer)
            : `${lastSeenText(analyzer)}, version ${analyzer.connection.connectorVersion}`}
        </dd>
        <dt>Waiting on the connector</dt>
        <dd>
          {analyzer.connection.spooled === null
            ? 'Not reported'
            : `${String(analyzer.connection.spooled)} messages`}
        </dd>
        <dt>Last message</dt>
        <dd>
          {analyzer.lastMessageAt === null ? 'None yet' : formatInstant(analyzer.lastMessageAt)}
        </dd>
        <dt>Credential issued</dt>
        <dd>{formatInstant(analyzer.credentialIssuedAt)}</dd>
      </dl>
      {next === null ? null : <Notice tone="info">{next}</Notice>}
      {analyzer.lastError === null ? null : (
        <Notice tone="info">{`${formatInstant(analyzer.lastError.at)}: ${analyzer.lastError.text}`}</Notice>
      )}
      {rotating ? (
        <RotateDialog
          analyzer={analyzer}
          onClose={() => {
            setRotating(false);
          }}
        />
      ) : null}
      {retiring ? (
        <RetireDialog
          analyzer={analyzer}
          onClose={() => {
            setRetiring(false);
          }}
        />
      ) : null}
    </Section>
  );
}

function RotateDialog({ analyzer, onClose }: { analyzer: AnalyzerView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [credential, setCredential] = useState<string | null>(null);
  const rotate = useMutation({
    mutationFn: () => rotateAnalyzerCredential(analyzer.id),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: analyzerKeys.all });
      setCredential(result.credential);
      toasts.show({ title: 'Credential rotated' });
    },
  });
  return (
    <Dialog
      title="Rotate credential"
      isOpen
      // While the credential shows, this is its only copy: only Done closes the dialog.
      isDismissable={credential === null}
      isKeyboardDismissDisabled={credential !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        {credential === null ? (
          <p>
            The current credential stops working at once. Results wait on the connector until the
            new one is installed.
          </p>
        ) : (
          <CredentialShownOnce credential={credential} />
        )}
        {rotate.isError ? (
          <Notice tone="error">
            {errorText(rotate.error, "The credential wasn't rotated. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        {credential === null ? (
          <>
            <Button variant="quiet" onPress={onClose}>
              Keep it
            </Button>
            <Button
              variant="primary"
              isPending={rotate.isPending}
              pendingLabel="Rotating"
              onPress={() => {
                rotate.mutate();
              }}
            >
              Rotate credential
            </Button>
          </>
        ) : (
          <Button variant="primary" onPress={onClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function RetireDialog({ analyzer, onClose }: { analyzer: AnalyzerView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const retire = useMutation({
    mutationFn: () => retireAnalyzer(analyzer.id, reason.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: analyzerKeys.all });
      toasts.show({ title: 'Analyzer retired' });
      onClose();
    },
  });
  const short = reason.trim().length < 5;
  return (
    <Dialog
      title="Retire analyzer"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p>
          Its credential stops working at once and it can&apos;t be used again. Results it sent stay
          in the lab&apos;s records.
        </p>
        <TextArea
          label="Reason"
          value={reason}
          onChange={setReason}
          {...(tried && short ? { errorMessage: 'Say why, in a few words.' } : {})}
        />
        {retire.isError ? (
          <Notice tone="error">
            {errorText(retire.error, "The analyzer wasn't retired. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Keep it
        </Button>
        <Button
          variant="primary"
          isPending={retire.isPending}
          pendingLabel="Retiring"
          onPress={() => {
            setTried(true);
            if (!short) retire.mutate();
          }}
        >
          Retire analyzer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MessageLog({ analyzerId }: { analyzerId: string }) {
  const messages = useQuery(analyzerMessagesQuery(analyzerId));
  return (
    <Section
      title="Message log"
      description="The last 20 messages and problems, without sample numbers or values."
    >
      <QueryState query={messages} what="message log" />
      {messages.isSuccess && messages.data.length === 0 ? (
        <EmptyState>Nothing yet. Messages appear once the connector starts.</EmptyState>
      ) : null}
      {messages.isSuccess && messages.data.length > 0 ? (
        <TableScroll>
          <Table aria-label="Message log" flat>
            <TableHeader>
              <Column isRowHeader>Time</Column>
              <Column>Outcome</Column>
              <Column>What happened</Column>
            </TableHeader>
            <TableBody items={messages.data}>
              {(m) => (
                <Row id={m.id}>
                  <Cell>{formatInstant(m.at)}</Cell>
                  <Cell>{m.outcome === 'succeeded' ? 'Received' : 'Problem'}</Cell>
                  <Cell>{m.text}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
    </Section>
  );
}
